/* eslint-disable jsx-a11y/alt-text */
import { FC, MouseEventHandler, ReactNode, useState } from 'react';

import { IconType } from 'react-icons';

import Link from 'next/link';
import Image, { StaticImageData } from 'next/image';

import { TColorProp } from 'styled-components';
import { Tooltip } from '@mui/material';

import {
  darkDynamicColors,
  getDynamicThemeColors,
  staticColors,
} from '~/common/styles/theme';
import { useMediaQuery } from '~/common/hooks/use-media-query';

import { ButtonContainer, LoaderWrapper } from './styles';
import { Typography } from '../Typograph';
import { Loader } from '../Loader';

type TButtonsHeight = '48' | '40' | '36' | '32' | '28' | '24' | '20' | '16';

export interface IButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  width?: string;
  id?: string;
  circular?: boolean;
  backgroundColor?: string;
  textOrIconColor?: string;
  textOrIconColorOnHover?: string;
  borderColor?: string;
  customCssBorderHover?: string;
  withArrowLeft?: boolean;
  withArrowRight?: boolean;
  hoverBackgroundColor?: string;
  disabled?: boolean;
  height?: TButtonsHeight;
  iconSize?: number | string;
  icon?: IconType;
  image?: string | StaticImageData;
  CustomIcon?: ReactNode;
  text?: string;
  tooltip?: string;
  justifyContent?: string;
  href?: string;
  loading?: boolean;
  loaderColor?: TColorProp;
  containerPadding?: number;
  boxShadow?: string;
  type?: 'button' | 'reset' | 'submit';
  loader?: 'puff' | 'bounce' | 'clip' | 'scale' | 'bar';
}

export const Button: FC<IButtonProps> = ({
  id,
  onClick = () => null,
  width = 'min-content',
  image,
  loading = false,
  containerPadding = 10,
  boxShadow,
  hoverBackgroundColor = null,
  backgroundColor = darkDynamicColors.primary,
  textOrIconColor = getDynamicThemeColors('light').neutral0,
  textOrIconColorOnHover = textOrIconColor,
  borderColor = darkDynamicColors.primary,
  customCssBorderHover,
  circular = false,
  disabled = false,
  type = 'button',
  justifyContent = 'space-between',
  height = '40',
  iconSize = 16,
  icon: Icon,
  CustomIcon,
  text,
  href,
  tooltip,
  loader = 'puff',
  loaderColor,
}) => {
  const isMobile = useMediaQuery('(max-width: 750px)');
  const [hover, setHover] = useState<boolean>(false);

  const Element = (
    <ButtonContainer
      type={type}
      id={id}
      hoverBackgroundColor={hoverBackgroundColor}
      circular={circular}
      boxShadow={boxShadow}
      containerPadding={containerPadding}
      width={width}
      backgroundColor={backgroundColor}
      textOrIconColorOnHover={textOrIconColorOnHover}
      textColor={textOrIconColor}
      borderColor={borderColor}
      disabled={disabled || loading}
      customCssBorderHover={customCssBorderHover}
      justifyContent={justifyContent}
      height={`${height}px`}
      onClick={onClick}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        maxHeight: `${height}px !important`,
        minHeight: `${height}px !important`,
        maxWidth:
          width === 'min-content'
            ? 'max-content !important'
            : `${width} !important`,
        minWidth:
          width === 'min-content'
            ? 'max-content !important'
            : `${width} !important`,
      }}
    >
      {!loading ? (
        <>
          {Icon && (
            <Icon
              color={hover ? textOrIconColorOnHover : textOrIconColor}
              size={iconSize}
            />
          )}
          {image && <Image alt="img" src={image} width={20} height={20} />}
          {CustomIcon}
          {text && <Typography.Label size="medium">{text}</Typography.Label>}
        </>
      ) : (
        <LoaderWrapper>
          <Loader size={Number(height) - 6} type={loader} color={loaderColor} />
        </LoaderWrapper>
      )}
    </ButtonContainer>
  );

  const WithOrWithoutTooltip =
    tooltip && !isMobile ? (
      <Tooltip title={tooltip} arrow>
        {Element}
      </Tooltip>
    ) : (
      Element
    );

  return (
    <>
      {href ? (
        <>
          <Link href={href} passHref>
            {WithOrWithoutTooltip}
          </Link>
        </>
      ) : (
        <>{WithOrWithoutTooltip}</>
      )}
    </>
  );
};
